import { fmtMXNFull } from "@/lib/api";

const MESES = ["ENERO","FEBRERO","MARZO","ABRIL","MAYO","JUNIO","JULIO","AGOSTO","SEPTIEMBRE","OCTUBRE","NOVIEMBRE","DICIEMBRE"];
const fechaLarga = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return `A ${d} DE ${MESES[m - 1]} DEL ${y}`;
};

const STATUS_LABEL = { borrador: "BORRADOR", aprobada: "APROBADA", rechazada: "RECHAZADA", terminada: "TERMINADA", pagada: "PAGADA" };
const STATUS_COLOR = { borrador: "#64748b", aprobada: "#16a34a", rechazada: "#dc2626", terminada: "#0891b2", pagada: "#db2777" };

const PRINT_COLORS_CSS = `
  @media print {
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    @page { size: letter portrait; margin: 4mm; }
    html, body { background: #ffffff !important; }
    body * { visibility: hidden; }
    [data-print-sheet], [data-print-sheet] * { visibility: visible; }
    [data-status-badge] { display: none !important; }
    [data-print-sheet] { position: absolute !important; left: 0 !important; top: 0 !important; right: 0 !important; width: 100% !important; max-width: 100% !important; margin: 0 !important; box-shadow: none !important; }
  }
`;

export const WorkshopQuoteSheet = ({ quote, settings, photoUrl, priceMode = "internal", hideStatus = false }) => {
  const s = settings || {};
  const brand = s.color || "#1d4ed8";
  const v = quote.vehicle || {};
  const isPublic = priceMode === "public";
  const itPrice = (it) => (isPublic && it.public_price != null ? it.public_price : it.price);
  const itTotal = (it) => (isPublic && it.public_total != null ? it.public_total : it.total);
  const subtotal = isPublic && quote.public_subtotal != null ? quote.public_subtotal : quote.subtotal;
  const iva = isPublic && quote.public_iva != null ? quote.public_iva : quote.iva;
  const total = isPublic && quote.public_total != null ? quote.public_total : quote.total;
  const hasPublic = !isPublic && (quote.items || []).some((it) => it.public_price != null && it.public_price !== it.price);
  const utilidad = quote.utilidad != null ? quote.utilidad : (quote.public_total ?? quote.total) - quote.total;

  return (
    <div className="bg-white text-black mx-auto" data-print-sheet style={{ maxWidth: 820, fontFamily: "Arial, sans-serif", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} data-testid="workshop-quote-sheet">
      <style>{PRINT_COLORS_CSS}</style>
      <div className="px-6 py-4">
        {/* Membrete */}
        <div className="flex items-start justify-between pb-2" style={{ borderBottom: `3px solid ${brand}` }}>
          <div className="flex items-center gap-3">
            {s.logo_file_id && photoUrl && (
              <img src={photoUrl(s.logo_file_id)} alt="logo" className="object-contain" style={{ height: 48, maxWidth: 100 }} data-testid="sheet-logo" />
            )}
            <div>
              <div className="font-bold text-xl leading-tight" style={{ color: brand }}>{s.name || "TALLER AUTOMOTRIZ"}</div>
              {s.slogan && <div className="text-[10px] font-semibold tracking-widest mt-0.5" style={{ color: "#334155" }}>✓ {s.slogan}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-lg tracking-widest" style={{ color: brand }}>COTIZACIÓN</div>
            <div className="text-sm font-mono font-bold">{quote.folio}</div>
            <div className="text-[10px] mt-0.5">{(s.city || "").toUpperCase()}, {fechaLarga(quote.date)}</div>
            {!hideStatus && (
              <div className="mt-1 inline-block text-[10px] font-bold px-2 py-0.5 rounded" data-status-badge style={{ background: STATUS_COLOR[quote.status], color: "white" }}>
                {STATUS_LABEL[quote.status] || quote.status}
              </div>
            )}
            {hasPublic && (
              <div className="mt-1" data-testid="sheet-internal-badge">
                <span className="inline-block text-[9px] font-bold px-2 py-0.5 rounded" style={{ background: "#f59e0b", color: "#1c1006" }}>VERSIÓN INTERNA · COSTOS REALES</span>
              </div>
            )}
            {quote.status === "pagada" && quote.payment_date && (
              <div className="text-[10px] mt-1 font-semibold" style={{ color: "#db2777" }} data-testid="sheet-payment-info">
                PAGADO {quote.payment_date.split("-").reverse().join("/")}{quote.payment_method ? ` · ${quote.payment_method}` : ""}
              </div>
            )}
          </div>
        </div>

        {/* Destinatario */}
        <div className="text-center mt-2 text-[12px] leading-tight">
          <div className="font-bold tracking-wider">DATOS DE COTIZACIÓN · {v.client_name ? <span data-testid="sheet-client-name">CLIENTE: {v.client_name}</span> : "A QUIEN CORRESPONDA"} · <span className="font-semibold">P R E S E N T E</span></div>
        </div>

        <p className="mt-1.5 text-[12px]">
          A continuación, le cotizamos los siguientes servicios{quote.service_description ? <> de <b>{quote.service_description}</b></> : ""} del vehículo:
        </p>

        {/* Vehículo */}
        <div className="mt-1.5 px-3 py-1.5 text-[12px]" style={{ border: "1px solid #cbd5e1", background: "#f8fafc" }}>
          <div className="grid grid-cols-2 gap-x-6" style={{ rowGap: 1 }}>
            <div><b>VEHÍCULO:</b> {v.brand} {v.model} {v.year || ""}</div>
            <div><b>PLACAS:</b> {v.plates || "—"}</div>
            <div><b>No. SERIE:</b> {v.vin || "—"}</div>
            <div><b>KILOMETRAJE:</b> {v.km != null ? `KM ${Number(v.km).toLocaleString("es-MX")}` : "—"}</div>
          </div>
        </div>

        {/* Conceptos */}
        <table className="w-full mt-2 text-[11.5px]" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: brand, color: "white" }}>
              <th className="text-left px-2 py-0.5 text-[10.5px]">CONCEPTO</th>
              <th className="text-center px-2 py-0.5 text-[10.5px]" style={{ width: 70 }}>CANTIDAD</th>
              <th className="text-center px-2 py-0.5 text-[10.5px]" style={{ width: 75 }}>UNIDAD</th>
              <th className="text-right px-2 py-0.5 text-[10.5px]" style={{ width: hasPublic ? 80 : 95 }}>{hasPublic ? "COSTO" : "PRECIO"}</th>
              <th className="text-right px-2 py-0.5 text-[10.5px]" style={{ width: hasPublic ? 80 : 95 }}>TOTAL</th>
              {hasPublic && <th className="text-right px-2 py-0.5 text-[10.5px]" style={{ width: 80 }}>P. PÚBLICO</th>}
              {hasPublic && <th className="text-right px-2 py-0.5 text-[10.5px]" style={{ width: 80 }}>UTILIDAD</th>}
            </tr>
          </thead>
          <tbody>
            {(quote.items || []).map((it, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <td className="px-2 py-[1px] uppercase leading-tight">{it.concept}</td>
                <td className="px-2 py-[1px] text-center">{it.qty}</td>
                <td className="px-2 py-[1px] text-center uppercase">{it.unit}</td>
                <td className="px-2 py-[1px] text-right">{fmtMXNFull(itPrice(it))}</td>
                <td className="px-2 py-[1px] text-right font-semibold">{fmtMXNFull(itTotal(it))}</td>
                {hasPublic && <td className="px-2 py-[1px] text-right">{fmtMXNFull(it.public_total ?? it.total)}</td>}
                {hasPublic && <td className="px-2 py-[1px] text-right font-semibold" style={{ color: "#16a34a" }}>{fmtMXNFull(it.utilidad ?? 0)}</td>}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totales */}
        <div className="flex justify-end mt-1.5" style={{ breakInside: "avoid" }}>
          <table className="text-[12px]" style={{ minWidth: 230 }}>
            <tbody>
              <tr><td className="px-2 py-0.5 text-right font-semibold">SUBTOTAL:</td><td className="px-2 py-0.5 text-right">{fmtMXNFull(subtotal)}</td></tr>
              <tr><td className="px-2 py-0.5 text-right font-semibold">IVA ({quote.iva_rate}%):</td><td className="px-2 py-0.5 text-right">{fmtMXNFull(iva)}</td></tr>
              <tr style={{ borderTop: `2px solid ${brand}` }}>
                <td className="px-2 py-1 text-right font-bold">{hasPublic ? "TOTAL (COSTO):" : "TOTAL:"}</td>
                <td className="px-2 py-1 text-right font-bold" style={{ color: brand }}>{fmtMXNFull(total)}</td>
              </tr>
              {hasPublic && (
                <tr data-testid="sheet-public-total-row">
                  <td className="px-2 py-0.5 text-right font-semibold">TOTAL PÚBLICO:</td>
                  <td className="px-2 py-0.5 text-right font-bold">{fmtMXNFull(quote.public_total ?? quote.total)}</td>
                </tr>
              )}
              {hasPublic && (
                <tr data-testid="sheet-utility-row">
                  <td className="px-2 py-0.5 text-right font-semibold" style={{ color: "#16a34a" }}>UTILIDAD:</td>
                  <td className="px-2 py-0.5 text-right font-bold" style={{ color: "#16a34a" }}>{fmtMXNFull(utilidad)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Observaciones */}
        {quote.observations && (
          <div className="mt-3 text-[13px]" data-testid="sheet-observations">
            <div className="font-bold text-xs tracking-widest px-2 py-1" style={{ background: "#f1f5f9", borderLeft: `3px solid ${brand}` }}>OBSERVACIONES</div>
            <p className="mt-1 px-2 whitespace-pre-wrap text-[12px]" style={{ color: "#334155" }}>{quote.observations}</p>
          </div>
        )}

        {/* Firma */}
        <div className="mt-5 text-[12px] grid" style={{ gridTemplateColumns: quote.client_signature ? "1fr 1fr" : "1fr", gap: 24, breakInside: "avoid" }}>
          <div className="text-center">
            <div className="tracking-widest">A T E N T A M E N T E</div>
            <div className="mt-6 mx-auto" style={{ borderTop: "1px solid #334155", width: 260 }} />
            <div className="font-bold mt-1">{s.signature_name || s.name}</div>
          </div>
          {quote.client_signature && (
            <div className="text-center" data-testid="client-signature-block">
              <div className="tracking-widest">AUTORIZÓ (CLIENTE)</div>
              <img src={quote.client_signature} alt="firma cliente" className="mx-auto mt-2" style={{ height: 55, objectFit: "contain" }} />
              <div className="mx-auto" style={{ borderTop: "1px solid #334155", width: 260 }} />
              <div className="font-bold mt-1">{quote.client_authorized_name}</div>
              {quote.client_authorized_at && <div className="text-[10px] text-slate-600">{new Date(quote.client_authorized_at).toLocaleString("es-MX")}</div>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-1.5 text-center text-[10px]" style={{ borderTop: `2px solid ${brand}`, color: "#334155" }}>
          {s.address}{s.address && s.city ? " · " : ""}{s.city}
        </div>
      </div>
    </div>
  );
};

export const WorkshopPhotoReport = ({ quote, settings, photoUrl }) => {
  const s = settings || {};
  const brand = s.color || "#1d4ed8";
  const antes = (quote.photos || []).filter((p) => p.phase === "antes");
  const despues = (quote.photos || []).filter((p) => p.phase === "despues");

  return (
    <div className="bg-white text-black mx-auto" data-print-sheet style={{ maxWidth: 820, fontFamily: "Arial, sans-serif", WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }} data-testid="workshop-photo-report">
      <style>{PRINT_COLORS_CSS}</style>
      <div className="px-8 py-6">
        <div className="flex items-center justify-between pb-2" style={{ borderBottom: `3px solid ${brand}` }}>
          <div className="flex items-center gap-3">
            {s.logo_file_id && photoUrl && (
              <img src={photoUrl(s.logo_file_id)} alt="logo" className="object-contain" style={{ height: 48, maxWidth: 100 }} />
            )}
            <div className="font-bold text-lg" style={{ color: brand }}>{s.name || "TALLER AUTOMOTRIZ"}</div>
          </div>
          <div className="text-right text-sm font-mono font-bold">{quote.folio}</div>
        </div>
        <div className="font-bold text-lg text-center tracking-wider mt-4" style={{ color: brand }}>REPORTE FOTOGRÁFICO — {quote.folio}</div>
        <div className="text-center text-[12px] mt-0.5" style={{ color: "#334155" }}>
          {quote.vehicle?.brand} {quote.vehicle?.model} {quote.vehicle?.year || ""} · PLACAS {quote.vehicle?.plates || "—"}
        </div>
        {antes.length === 0 && despues.length === 0 && (
          <div className="text-center text-sm mt-8" style={{ color: "#64748b" }}>Sin fotos registradas todavía.</div>
        )}
        {[["ANTES", antes, "#dc2626"], ["DESPUÉS", despues, "#16a34a"]].map(([label, list, color]) => list.length > 0 && (
          <div key={label} className="mt-4">
            <div className="font-bold text-sm px-2 py-1" style={{ background: color, color: "white" }}>{label} ({list.length})</div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {list.map((p) => (
                <div key={p.file_id} style={{ breakInside: "avoid" }}>
                  <img src={photoUrl(p.file_id)} alt={label} className="w-full object-cover rounded" style={{ height: 170, border: "1px solid #cbd5e1" }} />
                  {p.note && <div className="text-[10px] mt-0.5 text-slate-600">{p.note}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="mt-6 pt-2 text-center text-[10px]" style={{ borderTop: `2px solid ${brand}`, color: "#334155" }}>
          {s.address}{s.address && s.city ? " · " : ""}{s.city}
        </div>
      </div>
    </div>
  );
};

export default WorkshopQuoteSheet;
